import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Divider,
  TextField,
  Tooltip,
  Link,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Slide,
  Alert,
  useTheme,
} from "@mui/material";
import "../styles/rundetails.css";
import Snackbar from "@mui/material/Snackbar";
import PipelineRunDetails from "./PipelineRunDetails";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Layout from "./Layout";

const RunDetails = () => {
  const { runId } = useParams();
  const [runDetail, setRunDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [submittedText, setSubmittedText] = useState("");
  const token = localStorage.getItem("access_token");
  const OrgType = localStorage.getItem("org_type");
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const [open, setOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const baseUrl = process.env.REACT_APP_API_NEW_HOST_URL;
  const feedbackApi = `${baseUrl}/athrv/api/v1/platform/notes?run_id=${runId}`;

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const response = await fetch(feedbackApi, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          if (data?.data?.notes) {
            setFeedbackText(data.data.notes);
            setSubmitted(true);
          } else {
            setFeedbackText("");
            setSubmitted(false);
          }
        } else {
          console.error(
            `Feedback fetch failed with status : ${response.status}`
          );
        }
      } catch (error) {
        console.error("Error fetching feedback:", error);
      }
    };
    fetchFeedback();
  }, [feedbackApi]);

  const handleOpen = () => {
    setOpen(true);
    setEditMode(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim()) {
      showSnackbar("Please enter feedback before submitting.", "warning");
      return;
    }

    try {
      const response = await fetch(feedbackApi, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ notes: feedbackText }),
      });

      if (!response.ok) throw new Error("Submit failed");

      setSubmitted(true);
      setOpen(false);
      showSnackbar("✅ Feedback submitted successfully!", "success");
    } catch (error) {
      console.error("Submit Error:", error);
      showSnackbar("❌ Failed to submit feedback.", "error");
    }
  };

  const handleUpdateFeedback = async () => {
    try {
      const response = await fetch(feedbackApi, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ notes: feedbackText }),
      });

      if (!response.ok) throw new Error("Update failed");

      setOpen(false);
      setEditMode(false);
      showSnackbar("✅ Feedback updated successfully!", "success");
    } catch (error) {
      console.error("Update Error:", error);
      showSnackbar("❌ Failed to update feedback.", "error");
    }
  };

  const handleDeleteFeedback = async () => {
    try {
      const response = await fetch(feedbackApi, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Delete failed");

      setFeedbackText("");
      setSubmitted(false);
      showSnackbar("🗑️ Feedback deleted successfully!", "info");
    } catch (error) {
      console.error("Delete Error:", error);
      showSnackbar("❌ Failed to delete feedback.", "error");
    }
  };

  useEffect(() => {
    fetch(
      `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/projects/run-details/${runId}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        console.log("API Response:", data);
        setRunDetail(data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching run details:", error);
        setIsLoading(false);
      });
  }, [runId]);

  const formatTime = (timestamp) =>
    new Date(parseInt(timestamp) * 1000).toLocaleString();

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {isLoading ? (
          <Box sx={{ mt: 2 }}>
            <Skeleton variant="text" width="60%" height={30} />
            <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
          </Box>
        ) : runId != runDetail.run_id ? (
          <Box sx={{ p: 2 }} align="center">
            <Typography variant="h6" align="center" color="error">
              Run details not found
            </Typography>
            <Button
              variant="contained"
              className="athrv-dash-primary-button"
              align="center"
              sx={{ mt: 3 }}
              onClick={() => navigate(-1)}
            >
              Go Back
            </Button>
          </Box>
        ) : (
          <>
            <PipelineRunDetails />
            <Paper elevation={3} sx={{ mt: 2, p: 3, borderRadius: 2 }}>
              <Box
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 1, gap: 2 }}
              >
                <Typography variant="h5" gutterBottom>
                  Run Details
                </Typography>

                <Box
                  display="flex"
                  flexDirection={{ xs: "column", sm: "row" }}
                  justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  gap={2}
                >
                  <Button
                    variant="contained"
                    className="athrv-dash-primary-button"
                    onClick={() => navigate(`/projects/${runDetail.project_id}`, { state: { activeTab: 0 } })}
                  >
                    Back to Project
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleOpen}
                    className="athrv-dash-primary-button"
                  >
                    Scratchpad
                  </Button>
                </Box>
              </Box>

              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="body1" align="left">
                  <strong>Run ID:</strong> {runId}
                </Typography>
              </Box>

              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="body1" align="left">
                  <strong>Project Name:</strong> {runDetail.project_name}
                </Typography>
              </Box>

              <Typography variant="body1" align="left" gutterBottom>
                <strong>Timestamp:</strong> {formatTime(runDetail.createdAt)}
              </Typography>
              <Typography variant="body1" align="left" gutterBottom>
                <strong>reportStatus:</strong> {runDetail.reportStatus}
              </Typography>

              <Divider sx={{ my: 3 }} />

              {/* Feedback Form */}
              <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>
                  <Typography variant="h6">
                    {editMode
                      ? "Edit Notes"
                      : submitted && feedbackText
                        ? "Your Notes"
                        : "Submit Notes"}
                  </Typography>
                  <IconButton
                    onClick={handleClose}
                    sx={{ position: "absolute", top: 8, right: 8 }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>

                <DialogContent>
                  {!submitted || editMode ? (
                    <>
                      <TextField
                        label="Your Notes"
                        fullWidth
                        multiline
                        minRows={4}
                        variant="outlined"
                        margin="normal"
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                      />
                      <Box mt={3} display="flex" justifyContent="flex-end">
                        <Button
                          variant="contained"
                          color={editMode ? "success" : "primary"}
                          onClick={
                            editMode
                              ? handleUpdateFeedback
                              : handleSubmitFeedback
                          }
                          disabled={!feedbackText.trim()}
                        >
                          {editMode ? "Save" : "Submit"}
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {feedbackText}
                      </Typography>
                      <Box mt={2} display="flex" justifyContent="flex-end">
                        <IconButton
                          onClick={handleDeleteFeedback}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => setEditMode(true)}
                          color="primary"
                          sx={{ ml: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </DialogContent>
              </Dialog>

              <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
              >
                <Alert
                  onClose={handleSnackbarClose}
                  severity={snackbar.severity}
                  variant="filled"
                  sx={{ width: "100%" }}
                >
                  {snackbar.message}
                </Alert>
              </Snackbar>

              {runDetail.runDetails?.build &&
                ((OrgType !== "O" &&
                  (runDetail.runDetails.build.GenerictoolName ||
                    runDetail.runDetails.build.toolName)) ||
                  (OrgType === "O" && runDetail.runDetails.build.toolName)) ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    Build Details
                  </Typography>

                  <Typography
                    variant="body1"
                    color="InfoText"
                    align="left"
                    sx={{ mb: 3 }}
                  >
                    <strong
                      style={{
                        color: isDarkMode ? "#ffffff" : "inherit",
                      }}
                    >
                      Tool:{" "}
                      {OrgType !== "O"
                        ? runDetail.runDetails.build.GenerictoolName ||
                        runDetail.runDetails.build.toolName
                        : runDetail.runDetails.build.toolName}
                    </strong>
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <strong>Code Language:</strong>
                    </Grid>
                    <Grid item xs={9}>
                      {runDetail.runDetails.build.codeLanguage}
                    </Grid>

                    <Grid item xs={3}>
                      <strong>Build Status:</strong>
                    </Grid>
                    <Grid item xs={9}>
                      {runDetail.runDetails.build.buildStatus}
                    </Grid>

                    {runDetail.runDetails.build.buildStatus === "failed" && (
                      <>
                        <Grid item xs={3}>
                          <strong>Build Error:</strong>
                        </Grid>
                        <Grid item xs={9}>
                          <Box
                            sx={{
                              border: "1px solid #ccc",
                              borderRadius: "4px",
                              padding: "8px",
                              overflow: "auto",
                              resize: "both",
                              width: "100%",
                              height: "150px",
                              backgroundColor: isDarkMode ? "#000000" : "#f9f9f9",
                              fontFamily: "monospace",
                              fontSize: "14px",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {runDetail.runDetails.build.buildError ||
                              "No errors"}
                          </Box>
                        </Grid>
                      </>
                    )}

                    <Grid item xs={3}>
                      <strong>Execution Time:</strong>
                    </Grid>
                    <Grid item xs={9}>
                      {`${parseFloat(
                        runDetail.runDetails.build.buildTime
                      ).toFixed(2)} seconds`}
                    </Grid>
                  </Grid>
                </>
              ) : (
                <Typography>No build details available</Typography>
              )}

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Memory Testing
              </Typography>
              {runDetail.runDetails?.memoryTesting?.length ? (
                runDetail.runDetails.memoryTesting.map((analysis, index) => (
                  <Box key={index} sx={{ mb: 3 }}>
                    <Typography
                      variant="body1"
                      color="InfoText"
                      align="left"
                      sx={{ mb: 3 }}
                    >
                      <strong style={{
                        color: isDarkMode ? "#ffffff" : "inherit",
                      }}>
                        Tool:{" "}
                        {OrgType === "O"
                          ? analysis.toolName || ""
                          : analysis.GenerictoolName || analysis.toolName || ""}
                      </strong>
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <strong>Status:</strong>
                      </Grid>
                      <Grid item xs={9}>
                        {analysis.memoryTestStatus}
                      </Grid>
<Grid item xs={3}>
          <strong>Report Status:</strong>
        </Grid>
        <Grid item xs={9}>
          {analysis.report.status || "N/A"}
        </Grid>                     <Grid item xs={3}>
                        <strong>Execution Time:</strong>
                      </Grid>
                      <Grid item xs={9}>
                        {analysis.memoryTestTime
                          ? `${parseFloat(analysis.memoryTestTime).toFixed(
                            2
                          )} seconds`
                          : "N/A"}
                      </Grid>

                      {analysis.memoryTestStatus === "failed" && (
                        <>
                          <Grid item xs={3}>
                            <strong>Error:</strong>
                          </Grid>
                          <Grid item xs={9}>
                            <Box
                              sx={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "8px",
                                overflow: "auto",
                                resize: "both",
                                width: "100%",
                                height: "150px",
                                backgroundColor: isDarkMode ? "#000000" : "#f9f9f9",
                                fontFamily: "monospace",
                                fontSize: "14px",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {analysis.memoryTestError || "No errors"}
                            </Box>
                          </Grid>
                        </>
                      )}

                      {/* Conditional Buttons for PDF and CSV Reports */}
                      {(analysis.pdf_file_path_s3 ||
                        analysis.csv_file_path_s3) && (
                          <>
                            <Grid item xs={3}>
                              <strong>Report:</strong>
                            </Grid>
                            <Grid item xs={9}>
                              <Box sx={{ display: "flex", gap: 2 }}>
                                {/* File Download Button */}
                                {(analysis.pdf_file_path_s3 ||
                                  analysis.csv_file_path_s3) && (
                                    <Button
                                      variant="outlined"
                                      color={
                                        analysis.pdf_file_path_s3
                                          ? "primary"
                                          : "primary"
                                      }
                                      onClick={async () => {
                                        const token =
                                          localStorage.getItem("access_token");
                                        const filePath =
                                          analysis.pdf_file_path_s3 ||
                                          analysis.csv_file_path_s3;
                                        const fileType = analysis.pdf_file_path_s3
                                          ? "pdf"
                                          : "csv";

                                        try {
                                          const response = await fetch(
                                            `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/reports/generate-presigned-url?run_id=${runId}&file_path_s3=${filePath}`,
                                            {
                                              method: "GET",
                                              headers: {
                                                Authorization: `Bearer ${token}`,
                                                Accept: "*/*",
                                              },
                                            }
                                          );
                                          const data = await response.json();

                                          if (data.presigned_url) {
                                            const link =
                                              document.createElement("a");
                                            link.href = data.presigned_url;
                                            link.setAttribute(
                                              "download",
                                              `Memory_Test_Report.${fileType}`
                                            );
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                          } else {
                                            console.error(
                                              "Failed to get presigned URL"
                                            );
                                          }
                                        } catch (error) {
                                          console.error(
                                            "Error downloading file:",
                                            error
                                          );
                                        }
                                      }}
                                    >
                                      {analysis.pdf_file_path_s3
                                        ? "Download PDF Report"
                                        : "Download CSV Report"}
                                    </Button>
                                  )}
                              </Box>
                            </Grid>
                          </>
                        )}
                    </Grid>
                  </Box>
                ))
              ) : (
                <Typography>No Memory Testing details available</Typography>
              )}

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Static Analysis
              </Typography>
              {runDetail.runDetails?.staticAnalysis?.length ? (
                runDetail.runDetails.staticAnalysis.map((analysis, index) => (
                  <Box key={index} sx={{ mb: 3 }}>
                    <Typography
                      variant="body1"
                      color="InfoText"
                      align="left"
                      sx={{ mb: 3 }}
                    >
                      <strong style={{
                        color: isDarkMode ? "#ffffff" : "inherit",
                      }}>
                        Tool:{" "}
                        {OrgType === "O"
                          ? analysis.toolName || ""
                          : analysis.GenerictoolName || analysis.toolName || ""}
                      </strong>
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <strong>Status:</strong>
                      </Grid>
                      <Grid item xs={9}>
                        {analysis.analysisStatus}
                      </Grid>
<Grid item xs={3}>
          <strong>Report Status:</strong>
        </Grid>
        <Grid item xs={9}>
          {analysis.report.status || "N/A"}
        </Grid>
                      <Grid item xs={3}>
                        <strong>Execution Time:</strong>
                      </Grid>
                      <Grid item xs={9}>
                        {analysis.analysisTime
                          ? `${parseFloat(analysis.analysisTime).toFixed(
                            2
                          )} seconds`
                          : "N/A"}
                      </Grid>

                      {analysis.analysisStatus === "failed" && (
                        <>
                          <Grid item xs={3}>
                            <strong>Error:</strong>
                          </Grid>
                          <Grid item xs={9}>
                            <Box
                              sx={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "8px",
                                overflow: "auto",
                                resize: "both",
                                width: "100%",
                                height: "150px",
                                backgroundColor: isDarkMode ? "#000000" : "#f9f9f9",
                                fontFamily: "monospace",
                                fontSize: "14px",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {analysis.analysisError || "No errors"}
                            </Box>
                          </Grid>
                        </>
                      )}

                      {/* Conditional Buttons for PDF and CSV Reports */}
                      {(analysis.pdf_file_path_s3 ||
                        analysis.csv_file_path_s3) && (
                          <>
                            <Grid item xs={3}>
                              <strong>Report:</strong>
                            </Grid>
                            <Grid item xs={9}>
                              <Box sx={{ display: "flex", gap: 2 }}>
                                {/* File Download Button */}
                                {(analysis.pdf_file_path_s3 ||
                                  analysis.csv_file_path_s3) && (
                                    <Button
                                      variant="outlined"
                                      color={
                                        analysis.pdf_file_path_s3
                                          ? "primary"
                                          : "primary"
                                      }
                                      onClick={async () => {
                                        const token =
                                          localStorage.getItem("access_token");
                                        const filePath =
                                          analysis.pdf_file_path_s3 ||
                                          analysis.csv_file_path_s3;
                                        const fileType = analysis.pdf_file_path_s3
                                          ? "pdf"
                                          : "csv";

                                        try {
                                          const response = await fetch(
                                            `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/reports/generate-presigned-url?run_id=${runId}&file_path_s3=${filePath}`,
                                            {
                                              method: "GET",
                                              headers: {
                                                Authorization: `Bearer ${token}`,
                                                Accept: "*/*",
                                              },
                                            }
                                          );
                                          const data = await response.json();

                                          if (data.presigned_url) {
                                            const link =
                                              document.createElement("a");
                                            link.href = data.presigned_url;
                                            link.setAttribute(
                                              "download",
                                              `Static_Analysis_Report.${fileType}`
                                            );
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                          } else {
                                            console.error(
                                              "Failed to get presigned URL"
                                            );
                                          }
                                        } catch (error) {
                                          console.error(
                                            "Error downloading file:",
                                            error
                                          );
                                        }
                                      }}
                                    >
                                      {analysis.pdf_file_path_s3
                                        ? "Download PDF Report"
                                        : "Download CSV Report"}
                                    </Button>
                                  )}
                              </Box>
                            </Grid>
                          </>
                        )}
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                  </Box>
                ))
              ) : (
                <Typography>No static analysis details available</Typography>
              )}
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                Unit Testing
              </Typography>
              {runDetail.runDetails?.unitTesting?.length ? (
                runDetail.runDetails.unitTesting.map((testing, index) => (
                  <Box key={index} sx={{ mb: 3 }}>
                    <Typography
                      variant="body1"
                      color="InfoText"
                      align="left"
                      sx={{ mb: 3 }}
                    >
                      <strong style={{
                        color: isDarkMode ? "#ffffff" : "inherit",
                      }}>
                        Tool:{" "}
                        {OrgType === "O"
                          ? testing.toolName || ""
                          : testing.GenerictoolName || testing.toolName || ""}
                      </strong>
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <strong>Status:</strong>
                      </Grid>
                      <Grid item xs={9}>
                        {testing.testStatus}
                      </Grid>
<Grid item xs={3}>
          <strong>Report Status:</strong>
        </Grid>
        <Grid item xs={9}>
          {testing.report.status || "N/A"}
        </Grid>
                      <Grid item xs={3}>
                        <strong>Execution Time:</strong>
                      </Grid>
                      <Grid item xs={9}>
                        {testing.testTime
                          ? `${parseFloat(testing.testTime).toFixed(2)} seconds`
                          : "N/A"}
                      </Grid>

                      {testing.testStatus === "failed" && (
                        <>
                          <Grid item xs={3}>
                            <strong>Error:</strong>
                          </Grid>
                          <Grid item xs={9}>
                            <Box
                              sx={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "8px",
                                overflow: "auto",
                                resize: "both",
                                width: "100%",
                                height: "150px",
                                backgroundColor: isDarkMode ? "#000000" : "#f9f9f9",
                                fontFamily: "monospace",
                                fontSize: "14px",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {testing.testError || "No errors"}
                            </Box>
                          </Grid>
                        </>
                      )}

                      {/* Conditional Buttons for PDF and CSV Reports */}
                      {(testing.pdf_file_path_s3 ||
                        testing.csv_file_path_s3) && (
                          <>
                            <Grid item xs={3}>
                              <strong>Report:</strong>
                            </Grid>
                            <Grid item xs={9}>
                              <Box sx={{ display: "flex", gap: 2 }}>
                                {/* File Download Button */}
                                {(testing.pdf_file_path_s3 ||
                                  testing.csv_file_path_s3) && (
                                    <Button
                                      variant="outlined"
                                      color={
                                        testing.pdf_file_path_s3
                                          ? "primary"
                                          : "primary"
                                      }
                                      onClick={async () => {
                                        const token =
                                          localStorage.getItem("access_token");
                                        const filePath =
                                          testing.pdf_file_path_s3 ||
                                          testing.csv_file_path_s3;
                                        const fileType = testing.pdf_file_path_s3
                                          ? "pdf"
                                          : "csv";

                                        try {
                                          const response = await fetch(
                                            `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/reports/generate-presigned-url?run_id=${runId}&file_path_s3=${filePath}`,
                                            {
                                              method: "GET",
                                              headers: {
                                                Authorization: `Bearer ${token}`,
                                                Accept: "*/*",
                                              },
                                            }
                                          );
                                          const data = await response.json();

                                          if (data.presigned_url) {
                                            window.open(
                                              data.presigned_url,
                                              "_blank"
                                            );
                                          } else {
                                            console.error(
                                              "Failed to get presigned URL"
                                            );
                                          }
                                        } catch (error) {
                                          console.error(
                                            "Error fetching presigned URL:",
                                            error
                                          );
                                        }
                                      }}
                                    >
                                      {testing.pdf_file_path_s3
                                        ? "Download PDF Report"
                                        : "Download CSV Report"}
                                    </Button>
                                  )}
                              </Box>
                            </Grid>
                          </>
                        )}
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                  </Box>
                ))
              ) : (
                <Typography>No unit testing details available</Typography>
              )}
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                Cyber Security Analysis
              </Typography>
              {runDetail.runDetails?.cybersecurityAnalysis?.length ? (
                runDetail.runDetails.cybersecurityAnalysis.map((testing, index) => (
                  <Box key={index} sx={{ mb: 3 }}>
                    <Typography
                      variant="body1"
                      color="InfoText"
                      align="left"
                      sx={{ mb: 3 }}
                    >
                      <strong style={{
                        color: isDarkMode ? "#ffffff" : "inherit",
                      }}>
                        Tool:{" "}
                        {OrgType === "O"
                          ? testing.toolName || ""
                          : testing.GenerictoolName || testing.toolName || ""}
                      </strong>
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <strong>Status:</strong>
                      </Grid>
                      <Grid item xs={9}>
                        {testing.cybersecurityAnalysisStatus}
                      </Grid>
<Grid item xs={3}>
          <strong>Report Status:</strong>
        </Grid>
        <Grid item xs={9}>
          {testing.report.status || "N/A"}
        </Grid>
                      <Grid item xs={3}>
                        <strong>Execution Time:</strong>
                      </Grid>
                      <Grid item xs={9}>
                        {testing.cybersecurityAnalysisTime
                          ? `${parseFloat(testing.cybersecurityAnalysisTime).toFixed(2)} seconds`
                          : "N/A"}
                      </Grid>

                      {testing.testStatus === "failed" && (
                        <>
                          <Grid item xs={3}>
                            <strong>Error:</strong>
                          </Grid>
                          <Grid item xs={9}>
                            <Box
                              sx={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "8px",
                                overflow: "auto",
                                resize: "both",
                                width: "100%",
                                height: "150px",
                                backgroundColor: isDarkMode ? "#000000" : "#f9f9f9",
                                fontFamily: "monospace",
                                fontSize: "14px",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {testing.cybersecurityAnalysisError || "No errors"}
                            </Box>
                          </Grid>
                        </>
                      )}

                      {/* Conditional Buttons for PDF and CSV Reports */}
                      {(testing.pdf_file_path_s3 ||
                        testing.csv_file_path_s3) && (
                          <>
                            <Grid item xs={3}>
                              <strong>Report:</strong>
                            </Grid>
                            <Grid item xs={9}>
                              <Box sx={{ display: "flex", gap: 2 }}>
                                {/* File Download Button */}
                                {(testing.pdf_file_path_s3 ||
                                  testing.csv_file_path_s3) && (
                                    <Button
                                      variant="outlined"
                                      color={
                                        testing.pdf_file_path_s3
                                          ? "primary"
                                          : "primary"
                                      }
                                      onClick={async () => {
                                        const token =
                                          localStorage.getItem("access_token");
                                        const filePath =
                                          testing.pdf_file_path_s3 ||
                                          testing.csv_file_path_s3;
                                        const fileType = testing.pdf_file_path_s3
                                          ? "pdf"
                                          : "csv";

                                        try {
                                          const response = await fetch(
                                            `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/reports/generate-presigned-url?run_id=${runId}&file_path_s3=${filePath}`,
                                            {
                                              method: "GET",
                                              headers: {
                                                Authorization: `Bearer ${token}`,
                                                Accept: "*/*",
                                              },
                                            }
                                          );
                                          const data = await response.json();

                                          if (data.presigned_url) {
                                            window.open(
                                              data.presigned_url,
                                              "_blank"
                                            );
                                          } else {
                                            console.error(
                                              "Failed to get presigned URL"
                                            );
                                          }
                                        } catch (error) {
                                          console.error(
                                            "Error fetching presigned URL:",
                                            error
                                          );
                                        }
                                      }}
                                    >
                                      {testing.pdf_file_path_s3
                                        ? "Download PDF Report"
                                        : "Download CSV Report"}
                                    </Button>
                                  )}
                              </Box>
                            </Grid>
                          </>
                        )}
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                  </Box>
                ))
              ) : (
                <Typography>No Cyber Security analysis available</Typography>
              )}


<Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                Foss Compliance 
              </Typography>
              {runDetail.runDetails?.fossCompliance?.length ? (
                runDetail.runDetails.fossCompliance.map((testing, index) => (
                  <Box key={index} sx={{ mb: 3 }}>
                    <Typography
                      variant="body1"
                      color="InfoText"
                      align="left"
                      sx={{ mb: 3 }}
                    >
                      <strong style={{
                        color: isDarkMode ? "#ffffff" : "inherit",
                      }}>
                        Tool:{" "}
                        {OrgType === "O"
                          ? testing.toolName || ""
                          : testing.GenerictoolName || testing.toolName || ""}
                      </strong>
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <strong>Status:</strong>
                      </Grid>
                      <Grid item xs={3}>
          <strong>Report Status:</strong>
        </Grid>
        <Grid item xs={9}>
          {testing.report.status || "N/A"}
        </Grid>
                      <Grid item xs={9}>
                        {testing.analysisStatus}
                      </Grid>

                      <Grid item xs={3}>
                        <strong>Execution Time:</strong>
                      </Grid>
                      <Grid item xs={9}>
                        {testing.analysisTime
                          ? `${parseFloat(testing.analysisTime).toFixed(2)} seconds`
                          : "N/A"}
                      </Grid>

                      {testing.testStatus === "failed" && (
                        <>
                          <Grid item xs={3}>
                            <strong>Error:</strong>
                          </Grid>
                          <Grid item xs={9}>
                            <Box
                              sx={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "8px",
                                overflow: "auto",
                                resize: "both",
                                width: "100%",
                                height: "150px",
                                backgroundColor: isDarkMode ? "#000000" : "#f9f9f9",
                                fontFamily: "monospace",
                                fontSize: "14px",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {testing.analysisError || "No errors"}
                            </Box>
                          </Grid>
                        </>
                      )}

                      {/* Conditional Buttons for PDF and CSV Reports */}
                      {(testing.s3_pdf_file ||
                        testing.s3_csv_file) && (
                          <>
                            <Grid item xs={3}>
                              <strong>Report:</strong>
                            </Grid>
                            <Grid item xs={9}>
                              <Box sx={{ display: "flex", gap: 2 }}>
                                {/* File Download Button */}
                                {(testing.s3_pdf_file ||
                                  testing.s3_csv_file) && (
                                    <Button
                                      variant="outlined"
                                      color={
                                        testing.s3_pdf_file
                                          ? "primary"
                                          : "primary"
                                      }
                                      onClick={async () => {
                                        const token =
                                          localStorage.getItem("access_token");
                                        const filePath =
                                          testing.s3_pdf_file ||
                                          testing.s3_csv_file;
                                        const fileType = testing.s3_pdf_file
                                          ? "pdf"
                                          : "csv";

                                        try {
                                          const response = await fetch(
                                            `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/reports/generate-presigned-url?run_id=${runId}&file_path_s3=${filePath}`,
                                            {
                                              method: "GET",
                                              headers: {
                                                Authorization: `Bearer ${token}`,
                                                Accept: "*/*",
                                              },
                                            }
                                          );
                                          const data = await response.json();

                                          if (data.presigned_url) {
                                            window.open(
                                              data.presigned_url,
                                              "_blank"
                                            );
                                          } else {
                                            console.error(
                                              "Failed to get presigned URL"
                                            );
                                          }
                                        } catch (error) {
                                          console.error(
                                            "Error fetching presigned URL:",
                                            error
                                          );
                                        }
                                      }}
                                    >
                                      {testing.s3_pdf_file
                                        ? "Download PDF Report"
                                        : "Download CSV Report"}
                                    </Button>
                                  )}
                              </Box>
                            </Grid>
                          </>
                        )}
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                  </Box>
                ))
              ) : (
                <Typography>No Foss Compliance available</Typography>
              )}
              <Button
                sx={{ mt: 2 }}
                variant="contained"
                className="athrv-dash-primary-button"
                onClick={() => navigate(`/projects`)}
              >
                Back to Project List
              </Button>
            </Paper>
          </>
        )}
      </Box>
    </Layout>
  );
};

export default RunDetails;
